import React, { Suspense } from "react";
import Select, { components } from "react-select";

interface DropdownProps {
  options: { value: string; label: string }[];
  selectedValue: string | undefined;
  onValueChange: (selectedValue: string) => void;
  disabled: boolean;
  className?: string;
  placeholder: string;
  onFocus?: () => void;
  showOptionTooltips?: boolean;
  /** Accessible name for a dropdown with no visible label (e.g. a table filter). */
  ariaLabel?: string;
}

const TooltipOption = (props: any) => (
  <components.Option {...props}>
    <span title={props.data.label}>{props.children}</span>
  </components.Option>
);

const TooltipSingleValue = (props: any) => (
  <components.SingleValue {...props}>
    <span title={props.data.label}>{props.children}</span>
  </components.SingleValue>
);

const Dropdown: React.FC<DropdownProps> = ({
  options,
  selectedValue,
  onValueChange,
  disabled,
  className = "",
  placeholder,
  onFocus,
  showOptionTooltips = false,
  ariaLabel,
}) => {
  const customStyles = {
    control: (base: any) => ({
      ...base,
      border: "none",
      padding: "0",
      boxShadow: "none",
      backgroundColor: disabled ? "#f9f9f9" : "#fff",
      cursor: disabled ? "not-allowed" : "pointer",
      whiteSpace: "nowrap",
    }),
    option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isSelected ? "#007bff" : state.isFocused ? "#e9ecef" : "#fff",
        color: state.isSelected ? "#fff" : "#000",
        padding: "10px",
        cursor: "pointer",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }),
      
      menu: (base: any) => ({
        ...base,
        borderRadius: "8px",
        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
        minWidth: "250px",
        width: "auto",
        maxWidth: "100%",
        zIndex: 9999,
      }),
      menuPortal: (base: any) => ({
        ...base,
        zIndex: 9999,
      }),
      placeholder: (base: any) => ({
        ...base,
        whiteSpace: "nowrap", // Prevent wrapping of placeholder text
        overflow: "hidden", // Prevents text overflow
        textOverflow: "ellipsis", // Adds ellipsis for overflowing placeholder text
      }),
      
  };

  const handleChange = (selectedOption: any) => {
    onValueChange(selectedOption?.value || "");
  };
  

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Select
        value={
          options.find((option) => option.value === selectedValue) || null
        } 
        onChange={handleChange}
        options={options}
        isDisabled={disabled}
        styles={customStyles}
        classNamePrefix="custom-dropdown"
        className={className}
        placeholder={placeholder}
        menuPlacement="auto"
        menuPosition="fixed"
        menuPortalTarget={document.body}
        onFocus={onFocus}
        aria-label={ariaLabel}
        components={showOptionTooltips ? { Option: TooltipOption, SingleValue: TooltipSingleValue } : undefined}
      />
    </Suspense>
  );
};

export default Dropdown;
